import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  IconButton,
  useTheme,
  CircularProgress,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  KingBed as KingBedIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  Pool as PoolIcon,
  Room as RoomIcon,
  RoomService as RoomServiceIcon,
  LocalBar as BarIcon,
  Stars as StarsIcon,
  ChildCare as ChildCareIcon,
  Person as PersonIcon,
  Square as SquareIcon,
  ArrowBack as ArrowBackIcon,
  AcUnit as AcIcon,
  Tv as TvIcon,
  Coffee as CoffeeIcon,
  Iron as IronIcon,
  Bathtub as BathtubIcon,
  PhoneEnabled as PhoneIcon,
  Kitchen as KitchenIcon,
  Desk as DeskIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { getRoomById } from '../services/roomService'
import type { Room } from '../services/roomService'
import { motion } from 'framer-motion'
import BookingContactModal from './BookingContactModal'

const amenityIcons: { [key: string]: JSX.Element } = {
  'AC': <AcIcon />,
  'TV': <TvIcon />,
  'WiFi': <WifiIcon />,
  'Mini Bar': <BarIcon />,
  'Room Service': <RoomServiceIcon />,
  'Coffee Maker': <CoffeeIcon />,
  'Iron & Board': <IronIcon />,
  'Hair Dryer': <RoomServiceIcon />,
  'Safe': <RoomServiceIcon />,
  'Desk': <DeskIcon />,
  'Bathtub': <BathtubIcon />,
  'Shower': <BathtubIcon />,
  'Toiletries': <RoomServiceIcon />,
  'Towels': <RoomServiceIcon />,
  'Wardrobe': <RoomServiceIcon />,
  'Telephone': <PhoneIcon />,
  'Refrigerator': <KitchenIcon />,
  'Microwave': <KitchenIcon />,
}

const MotionBox = motion(Box)

const RoomDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { convertPrice } = useCurrency()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const theme = useTheme()
  const [room, setRoom] = useState<Room | null>(null)
  const [loading, setLoading] = useState(true)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  
  // Initialize booking details from navigation state or defaults
  const bookingDetails = location.state?.bookingDetails || {
    checkIn: new Date().toISOString().split('T')[0],
    checkOut: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    adults: 1,
    children: 0,
  }

  useEffect(() => {
    if (!id) return
    const room = getRoomById(id)
    setRoom(room ?? null)
    setLoading(false)
  }, [id])

  const handleNextImage = () => {
    if (!room) return
    setCurrentImageIndex((prev) => (prev + 1) % room.images.length)
  }

  const handlePrevImage = () => {
    if (!room) return
    setCurrentImageIndex((prev) => (prev - 1 + room.images.length) % room.images.length)
  }

  const handleBookNow = () => {
    setContactModalOpen(true)
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!room) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Room not found
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/rooms')}
          sx={{ mt: 3 }}
        >
          Back to Rooms
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ pb: 8 }}>
      <BookingContactModal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        roomName={room?.name}
      />
      {/* Room Images */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <MotionBox
          key={currentImageIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          sx={{
            height: { xs: '300px', md: '500px' },
            width: '100%',
            position: 'relative',
            bgcolor: 'grey.100',
          }}
        >
          <img
            src={room.images[currentImageIndex]}
            alt={`${room.name} - View ${currentImageIndex + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </MotionBox>

        {/* Navigation Arrows */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            background: 'linear-gradient(to right, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 15%, rgba(0,0,0,0) 85%, rgba(0,0,0,0.3) 100%)',
          }}
        >
          <IconButton
            onClick={handlePrevImage}
            sx={{
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <IconButton
            onClick={handleNextImage}
            sx={{
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' },
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>

        {/* Image Counter */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 2,
            py: 0.75,
            borderRadius: 2,
          }}
        >
          <Typography variant="body2">
            {currentImageIndex + 1} / {room.images.length}
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Back
        </Button>

        <Grid container spacing={4}>
          {/* Room Details */}
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom>
              {room.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {room.type}
            </Typography>

            <Typography variant="body1" paragraph>
              {room.description}
            </Typography>

            <Grid container spacing={4} sx={{ mb: 4 }}>
              <Grid item xs={6}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1">
                      Up to {room.capacity.adults} adults
                      {room.capacity.children > 0 && ` & ${room.capacity.children} children`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SquareIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1">
                      {room.size.squareFeet} sq.ft
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <KingBedIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body1">
                      {room.beds
                        .filter(bed => bed.count > 0)
                        .map(bed => `${bed.count} ${bed.type}`)
                        .join(', ')}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Additional Features
                </Typography>
              </Grid>
              {room.features.isSmokingAllowed && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Smoking Allowed" color="primary" variant="outlined" />
                  </Box>
                </Grid>
              )}
              {room.features.isPetFriendly && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Pet Friendly" color="primary" variant="outlined" />
                  </Box>
                </Grid>
              )}
              {room.features.hasBalcony && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Balcony" color="primary" variant="outlined" />
                  </Box>
                </Grid>
              )}
              {room.features.hasWindowView && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Window View" color="primary" variant="outlined" />
                  </Box>
                </Grid>
              )}
              {room.features.isAccessible && (
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Accessible" color="primary" variant="outlined" />
                  </Box>
                </Grid>
              )}
            </Grid>

            <Typography variant="h6" gutterBottom>
              Amenities
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 4 }}>
              {room.amenities.map((amenity) => (
                <Chip
                  key={amenity}
                  icon={amenityIcons[amenity] || <RoomServiceIcon />}
                  label={amenity}
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    '& .MuiChip-icon': {
                      color: 'primary.main',
                    },
                    borderColor: 'rgba(0,0,0,0.1)',
                  }}
                />
              ))}
            </Stack>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Policies
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Check-in Time
                  </Typography>
                  <Typography>{room.policies.checkInTime}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Check-out Time
                  </Typography>
                  <Typography>{room.policies.checkOutTime}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Cancellation Policy
                  </Typography>
                  <Typography>Free cancellation up to {room.policies.cancellationHours} hours before check-in</Typography>
                </Box>
                {room.policies.extraBedAllowed && (
                  <Box>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      Extra Bed
                    </Typography>
                    <Typography>
                      Available at {room.policies.extraBedCharge ? convertPrice(room.policies.extraBedCharge) : 'no additional cost'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Pricing and Booking */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                position: 'sticky',
                top: 120,
              }}
            >
              <Typography variant="h5" gutterBottom>
                {convertPrice(room.discountedPrice || room.basePrice)}
                {room.discountedPrice && (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      textDecoration: 'line-through',
                      color: 'text.secondary',
                      ml: 1,
                    }}
                  >
                    {convertPrice(room.basePrice)}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                per night
              </Typography>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleBookNow}
                sx={{
                  mt: 2,
                  py: 1.5,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                Book Now
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 2 }}
              >
                Free cancellation up to 24 hours before check-in
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default RoomDetails